import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMinus,
  faPlus,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { updateStore } from '../../api/branchStores';
import { getToken } from '../../services/auth';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import {
  OnboardingBoxStep1,
  OnboardingTitle,
  StepIndicator,
  OnboardingFormContainer,
  NavigationSection,
  NextButton,
  FormGrid,
  FormField,
  FieldGroup,
  FieldLabel,
  HelperText,
  StyledInput,
  RadioGroup,
  RadioOption,
  RadioInput,
  FileUploadContainer,
  FileUploadButton,
  FileUploadInput,
  FileUploadLabel,
  FileName,
  ClearAddressButton,
  InputWithClearContainer,
  AdditionalLocationsContainer,
  AdditionalLocationsQuestion,
  AdditionalLocationsOptions,
  CounterSection,
  CounterControlsContainer,
  CounterButton,
  CounterInput,
  CounterLabel,
  CounterHelpText,
  ErrorText,
  ErrorContainer,
  ValidationAlertIcon,
  Form,
} from './Onboarding.styled';

export const OnboardingBusiness = ({ onNext, initialData = {} }) => {
  const { t } = useTranslation();
  const branchStoreState = useSelector((state) => state.store);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    businessName:
      initialData.businessName ||
      (branchStoreState &&
        branchStoreState.store &&
        branchStoreState.store.name) ||
      '',
    businessType: initialData.businessType || 'groceries',
    businessLogo: null,
    hasAdditionalLocations:
      initialData.hasAdditionalLocations !== undefined
        ? initialData.hasAdditionalLocations
        : false,
    additionalLocationsCount: initialData.additionalLocationsCount || 1,
    ...initialData,
  });

  const updateHasAdditionalLocations = (count) => {
    return count > 1;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorLogo, setErrorLogo] = useState('');
  const [errors, setErrors] = useState({});

  const validateAlphanumeric = (value, maxLength = 100) => {
    if (!value || value.trim() === '')
      return t('Onboarding.validation.required');
    if (value.length > maxLength)
      return t('Onboarding.validation.maxLength', { maxLength });
    if (!/^[a-zA-Z0-9\s]+$/.test(value))
      return t('Onboarding.validation.alphanumeric');
    return '';
  };

  const validateFile = (file) => {
    if (!file) return '';

    if (!(file instanceof File)) return t('Onboarding.validation.invalidFile');

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      return t('Onboarding.validation.fileType');
    }

    if (file.size > 1024 * 1024) {
      return t('Onboarding.validation.fileSize');
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateAlphanumeric(formData.businessName, 100);
    if (nameError) newErrors.businessName = nameError;

    if (formData.businessLogo) {
      const logoError = validateFile(formData.businessLogo);
      if (logoError) newErrors.businessLogo = logoError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  React.useEffect(() => {
    const storeName =
      (branchStoreState &&
        branchStoreState.store &&
        branchStoreState.store.name) ||
      '';

    if (storeName && !formData.businessName && !initialData.businessName) {
      setFormData((prev) => ({
        ...prev,
        businessName: storeName,
      }));
    }
  }, [branchStoreState, formData.businessName, initialData.businessName]);

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }

    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    setErrorLogo('');
    if (errors.businessLogo) {
      setErrors((prev) => ({
        ...prev,
        businessLogo: '',
      }));
    }

    if (file && file instanceof File) {
      const fileError = validateFile(file);

      if (fileError) {
        setErrors((prev) => ({
          ...prev,
          businessLogo: fileError,
        }));
        event.target.value = null;
        setFormData({
          ...formData,
          businessLogo: null,
        });
      } else {
        setFormData({
          ...formData,
          businessLogo: file,
        });
      }
    } else {
      setFormData({
        ...formData,
        businessLogo: null,
      });
    }
  };

  const handleClearFile = () => {
    setFormData({
      ...formData,
      businessLogo: null,
    });

    setErrorLogo('');
    if (errors.businessLogo) {
      setErrors((prev) => ({
        ...prev,
        businessLogo: '',
      }));
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleCounterChange = (operation) => {
    let newCount = formData.additionalLocationsCount;

    if (operation === 'increment') {
      newCount = Math.min(newCount + 1, 5);
    } else if (operation === 'decrement') {
      newCount = Math.max(newCount - 1, 1);
    }

    setFormData({
      ...formData,
      additionalLocationsCount: newCount,
      hasAdditionalLocations: updateHasAdditionalLocations(newCount),
    });
  };

  const handleCounterInputChange = (event) => {
    const value = parseInt(event.target.value) || 1;
    const clampedValue = Math.min(Math.max(value, 1), 5);

    setFormData({
      ...formData,
      additionalLocationsCount: clampedValue,
      hasAdditionalLocations: updateHasAdditionalLocations(clampedValue),
    });
  };

  const mapBusinessTypeToStoreTypeId = (businessType) => {
    const typeMapping = {
      groceries: '1',
      restaurant: '2',
    };
    return typeMapping[businessType] || '1';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    if (!branchStoreState || !branchStoreState.id) {
      setError(
        'Error: No se encontró el ID del store. Por favor, recarga la página.',
      );
      setIsLoading(false);
      return;
    }

    if (!getToken()) {
      setError(
        'Error: No se encontró el token de autenticación. Por favor, inicia sesión nuevamente.',
      );
      setIsLoading(false);
      return;
    }

    try {
      let storeData;

      if (formData.businessLogo && formData.businessLogo instanceof File) {
        if (
          formData.businessLogo.type === 'image/jpeg' ||
          formData.businessLogo.type === 'image/jpg' ||
          formData.businessLogo.type === 'image/png'
        ) {
          storeData = new FormData();
          storeData.append('name', formData.businessName);
          storeData.append(
            'store_type_id',
            mapBusinessTypeToStoreTypeId(formData.businessType),
          );
          storeData.append('locations', formData.additionalLocationsCount);
          storeData.append('logo', formData.businessLogo);
        } else {
          setError('Error: Solo se permiten archivos JPG, JPEG y PNG.');
          setIsLoading(false);
          return;
        }
      } else {
        storeData = {
          name: formData.businessName,
          store_type_id: mapBusinessTypeToStoreTypeId(formData.businessType),
          locations: formData.additionalLocationsCount,
        };
      }

      await updateStore({
        id: branchStoreState.id,
        token: getToken(),
        params: storeData,
      });

      onNext(formData);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError(
          'Error de autenticación. Por favor, inicia sesión nuevamente.',
        );
      } else if (err.response && err.response.status === 400) {
        setError(
          'Datos inválidos. Por favor, verifica la información ingresada.',
        );
      } else {
        setError('Error al guardar los datos. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OnboardingBoxStep1>
      <OnboardingTitle>
        {t('BusinessForm.onboarding.step1.title')}
      </OnboardingTitle>

      <StepIndicator>
        {t('Onboarding.stepIndicator.step1', {
          totalSteps: 1 + formData.additionalLocationsCount,
        })}
      </StepIndicator>

      <OnboardingFormContainer>
        <Form onSubmit={handleSubmit}>
          <FormGrid>
            {/* Business Name - Full Width */}
            <FormField className="full-width">
              <FieldGroup>
                <FieldLabel>{t('Onboarding.business.businessName')}</FieldLabel>
                <StyledInput
                  type="text"
                  placeholder={t('Onboarding.business.businessNamePlaceholder')}
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange('businessName')}
                  maxLength="100"
                  required
                />
                {errors.businessName && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errors.businessName}</ErrorText>
                  </ErrorContainer>
                )}
              </FieldGroup>
            </FormField>

            {/* Logo Upload - Full Width */}
            <FormField className="full-width">
              <FieldGroup>
                <FieldLabel>{t('Onboarding.business.uploadLogo')}</FieldLabel>
                <InputWithClearContainer
                  showClear={formData.businessLogo !== null}>
                  <FileUploadContainer>
                    <FileUploadInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                    />
                    <FileUploadButton>
                      <FileUploadLabel>
                        {t('Onboarding.business.chooseFile')}
                      </FileUploadLabel>
                      <FileName>
                        {formData.businessLogo
                          ? formData.businessLogo.name
                          : t('Onboarding.business.noFileSelected')}
                      </FileName>
                    </FileUploadButton>
                  </FileUploadContainer>
                  {formData.businessLogo && (
                    <ClearAddressButton
                      type="button"
                      onClick={handleClearFile}
                      title={t('Onboarding.business.clearFile')}
                    />
                  )}
                </InputWithClearContainer>
                <HelperText>{t('Onboarding.business.fileHelper')}</HelperText>
                {errors.businessLogo && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errors.businessLogo}</ErrorText>
                  </ErrorContainer>
                )}
                {errorLogo && (
                  <ErrorContainer>
                    <ValidationAlertIcon>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </ValidationAlertIcon>
                    <ErrorText>{errorLogo}</ErrorText>
                  </ErrorContainer>
                )}
              </FieldGroup>
            </FormField>

            {/* Business Type - Full Width */}
            <FormField className="full-width">
              <FieldGroup>
                <FieldLabel>{t('Onboarding.business.businessType')}</FieldLabel>
                <RadioGroup>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="businessType"
                      value="groceries"
                      checked={formData.businessType === 'groceries'}
                      onChange={handleInputChange('businessType')}
                    />
                    {t('Onboarding.business.grocery')}
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="businessType"
                      value="restaurant"
                      checked={formData.businessType === 'restaurant'}
                      onChange={handleInputChange('businessType')}
                    />
                    {t('Onboarding.business.restaurant')}
                  </RadioOption>
                </RadioGroup>
              </FieldGroup>
            </FormField>

            {/* Additional Locations - Full Width */}
            <FormField className="full-width">
              <FieldGroup>
                <FieldLabel>
                  {t('Onboarding.business.additionalLocations')}
                </FieldLabel>

                <AdditionalLocationsContainer
                  isActive={formData.hasAdditionalLocations}>
                  <AdditionalLocationsQuestion>
                    {t('Onboarding.business.additionalLocationsQuestion')}
                  </AdditionalLocationsQuestion>

                  <AdditionalLocationsOptions
                    showCounter={formData.hasAdditionalLocations}>
                    <RadioOption>
                      <RadioInput
                        type="radio"
                        name="hasAdditionalLocations"
                        value="yes"
                        checked={formData.hasAdditionalLocations === true}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            hasAdditionalLocations: true,
                            additionalLocationsCount: Math.max(
                              formData.additionalLocationsCount,
                              2,
                            ),
                          })
                        }
                      />
                      {t('Onboarding.business.yes')}
                    </RadioOption>
                    <RadioOption>
                      <RadioInput
                        type="radio"
                        name="hasAdditionalLocations"
                        value="no"
                        checked={formData.hasAdditionalLocations === false}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            hasAdditionalLocations: false,
                            additionalLocationsCount: 1,
                          })
                        }
                      />
                      {t('Onboarding.business.no')}
                    </RadioOption>
                  </AdditionalLocationsOptions>

                  {formData.hasAdditionalLocations && (
                    <div>
                      <CounterSection>
                        <CounterLabel>
                          {t('Onboarding.business.howMany')}
                        </CounterLabel>
                        <CounterControlsContainer>
                          <CounterButton
                            type="button"
                            onClick={() => handleCounterChange('decrement')}
                            disabled={formData.additionalLocationsCount <= 1}>
                            <FontAwesomeIcon icon={faMinus} />
                          </CounterButton>
                          <CounterInput
                            type="number"
                            value={formData.additionalLocationsCount}
                            onChange={handleCounterInputChange}
                            min="1"
                            max="5"
                          />
                          <CounterButton
                            type="button"
                            onClick={() => handleCounterChange('increment')}
                            disabled={formData.additionalLocationsCount >= 5}>
                            <FontAwesomeIcon icon={faPlus} />
                          </CounterButton>
                        </CounterControlsContainer>
                      </CounterSection>
                      {FEATURE_FLAGS.ENABLE_BULK_LOCATIONS_CONTACT && (
                        <CounterHelpText>
                          {t('Onboarding.business.contactSupport')}
                        </CounterHelpText>
                      )}
                    </div>
                  )}
                </AdditionalLocationsContainer>
              </FieldGroup>
            </FormField>
          </FormGrid>

          {error && (
            <ErrorContainer
              style={{ justifyContent: 'center', marginBottom: '16px' }}>
              <ValidationAlertIcon>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </ValidationAlertIcon>
              <ErrorText>{error}</ErrorText>
            </ErrorContainer>
          )}

          <NavigationSection>
            <NextButton type="submit" disabled={isLoading}>
              {isLoading
                ? t('Onboarding.business.saving')
                : t('Onboarding.business.next')}
            </NextButton>
          </NavigationSection>
        </Form>
      </OnboardingFormContainer>
    </OnboardingBoxStep1>
  );
};
